/**
 * Mulligan API error class.
 *
 * Preserves the backend's stable error `code` (e.g. `WINDOW_OVERLAP`,
 * `OPEN_WINDOW_START_LOCKED`, `WINDOW_HAS_SWAPS`) so callers can map them
 * to user-friendly inline validation messages without parsing the message
 * string.
 *
 * Requirements: 1.2, 1.3, 1.4, 1.5, 1.7, 1.8, 1.10
 * Design: Frontend Components → MulliganWindowsAdminPage; Error Handling
 */
import type { ApiError } from './types';

/**
 * Error thrown by mulligan TanStack Query hooks when the backend returns
 * a non-2xx response. Carries the stable `code` and optional `details`
 * from the backend so the UI can map them to localized copy.
 */
export class MulliganApiError extends Error {
  public readonly code: string;
  public readonly details: Record<string, unknown> | undefined;

  constructor(apiError: ApiError) {
    super(apiError.message);
    this.name = 'MulliganApiError';
    this.code = apiError.code;
    this.details = apiError.details;
    // Required for `instanceof` to work after transpile (TS / ES5 quirk).
    Object.setPrototypeOf(this, MulliganApiError.prototype);
  }
}

/**
 * Type guard so callers can `if (isMulliganApiError(err)) { err.code ... }`.
 */
export function isMulliganApiError(err: unknown): err is MulliganApiError {
  return err instanceof MulliganApiError;
}
